import { create } from 'zustand'
import type { Analysis, Dialog, Metrics, Mode, Screen, Stroke } from '../lib/types'
import type { ToolState } from '../lib/board'
import type { CheckIn } from '../lib/api'

type SessionState = {
  screen: Screen
  /** экран, к которому возвращает вкладка «Рисовать» */
  flowScreen: Screen
  mode: Mode
  mood: number | null
  energy: number | null
  tags: string[]
  feelText: string

  strokes: Stroke[]
  exStrokes: Stroke[]
  beforePNG: string | null
  afterPNG: string | null
  captureMode: 'canvas' | 'photo'

  metricsBefore: Metrics | null
  metricsEx: Metrics | null
  analysis: Analysis | null
  calmArcs: number
  /** режим «дорисовать» поверх результата — без квот и оценок */
  drawOver: boolean

  entryId: number | null
  postDraft: string
  freeState: string
  finalState: string
  finalText: string
  freeText: string
  support: boolean
  aiFailed: boolean

  loadMsg: string
  /** открытая комната */
  activeDialog: Dialog | null
  /** комната, для которой сейчас рисуем ход */
  replyDialogId: string | null
  /** цитируемое сообщение для следующего хода */
  pendingReplyTo: number | null
  /** телеметрия рисунка-хода, чтобы связать его со штрихами */
  replySessionId: number | null
  /** сообщение, к которому прокрутить после открытия по глубокой ссылке */
  focusMsgId: number | null

  go: (s: Screen) => void
  reset: () => void
  set: (p: Partial<SessionState>) => void
}

const initial = {
  screen: 'home' as Screen,
  flowScreen: 'home' as Screen,
  mode: 'free' as Mode,
  mood: null,
  energy: null,
  tags: [] as string[],
  feelText: '',
  strokes: [] as Stroke[],
  exStrokes: [] as Stroke[],
  beforePNG: null,
  afterPNG: null,
  captureMode: 'canvas' as const,
  metricsBefore: null,
  metricsEx: null,
  analysis: null,
  calmArcs: 0,
  drawOver: false,
  entryId: null,
  postDraft: '',
  freeState: '',
  finalState: '',
  finalText: '',
  freeText: '',
  support: false,
  aiFailed: false,
  loadMsg: '',
  activeDialog: null,
  replyDialogId: null,
  pendingReplyTo: null,
  replySessionId: null,
  focusMsgId: null,
}

const FLOW_SCREENS: Screen[] = ['home', 'pre', 'draw', 'load', 'analysis', 'exercise', 'result', 'fresult']

export const useSession = create<SessionState>((set, get) => ({
  ...initial,
  go: (s) => {
    const st = get()
    const keepFlow = FLOW_SCREENS.includes(s) && !(s === 'draw' && st.mode === 'reply')
    set({ screen: s, flowScreen: keepFlow ? s : st.flowScreen })
    window.scrollTo(0, 0)
  },
  reset: () => set({ ...initial }),
  set: (p) => set(p),
}))

export const checkIn = (): CheckIn => {
  const s = useSession.getState()
  return { mood: s.mood, energy: s.energy, tags: s.tags, feelText: s.feelText }
}

/** Инструмент общий на всех холстах — одна палитра везде. */
export const useTools = create<ToolState & { set: (p: Partial<ToolState>) => void }>((set) => ({
  color: '#16181D',
  tool: 'pen',
  size: 1,
  set: (p) => set(p),
}))
